import { useState, useEffect } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleNotch } from "@fortawesome/free-solid-svg-icons";
import { BlobProvider } from "@react-pdf/renderer";
import { format } from "date-fns";

import { Loading } from "../../../components";
import { PDFDocument } from "../../../components/pdfIndividual";
import { RadarDEPs } from "../../../components/RadarDEPs";
import CardInfo from "../../../components/CardInfo";
import FamilyTree from "../../../components/FamilyTree";
import AvaliacaoGeneticaService from "../../../services/AvaliacaoGeneticaService";

export default function FichaAnimal({
  codAnimal,
  setLink,
}) {
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [detalhesAnimal, setDetalhesAnimal] = useState(null);
  const [gerarPDF, setGerarPDF] = useState(false);

  useEffect(() => {
    const lastSearch = JSON.parse(sessionStorage.getItem('DetalhesAnimal'))

    if (lastSearch) {
      if (lastSearch.cga === codAnimal) {
        setIsLoaded(true)
        // Dados podem ter sido salvos no sessionStorage por uma versao anterior
        // (antes de correcoes de robustez); normaliza os campos que devem ser
        // arrays para evitar quebrar o render caso o cache esteja incompleto.
        setDetalhesAnimal({
          ...lastSearch,
          caracteristicasAgrupadas: Array.isArray(lastSearch.caracteristicasAgrupadas) ? lastSearch.caracteristicasAgrupadas : [],
          genealogia: Array.isArray(lastSearch.genealogia) ? lastSearch.genealogia : [],
          classification: Array.isArray(lastSearch.classification) ? lastSearch.classification : [],
          section_01: Array.isArray(lastSearch.section_01) ? lastSearch.section_01 : [],
          section_02: Array.isArray(lastSearch.section_02) ? lastSearch.section_02 : [],
          percentilRaca: Array.isArray(lastSearch.percentilRaca) ? lastSearch.percentilRaca : [],
        });
      } else {
        init()
      }
    } else {
      init()
    }
  }, []);

  const init = async () => {
    try {
      const response = await AvaliacaoGeneticaService.detalhesAnimal(codAnimal)

      if (response) {
        if (response.isAxiosError) {
          setIsLoaded(true)
          setError(response.response.data.message || 'Erro Interno do Servidor')
          return
        }

        const { caracteristicasAgrupadas } = await AvaliacaoGeneticaService.helpersCaracteristicas(
          response.caracteristicas,
          codAnimal,
          response.animal.idRaca
        )
        const agAtual = await AvaliacaoGeneticaService.agAtual(response.animal.idRaca)
        // Tabela de percentis da raca do animal - da a referencia "Mediana da
        // Raça" no radar de DEPs. Se a chamada falhar, o service ja devolve
        // [] (ver AvaliacaoGeneticaService.tabelaPercentilPorRaca), e o radar
        // simplesmente mostra so a linha do animal, sem quebrar a tela.
        const percentilRaca = await AvaliacaoGeneticaService.tabelaPercentilPorRaca(response.animal.idRaca)

        const newModel = changeModel({
          ...response,
          caracteristicasAgrupadas,
          agAtual,
          percentilRaca,
        })

        setDetalhesAnimal(newModel);
        LastSearch(newModel)
      }
      setIsLoaded(true)
    } catch (error) {
      setIsLoaded(true)
      setError(
        (error && error.response && error.response.data && error.response.data.message) ||
        'Erro Interno do Servidor'
      )
    }
  }

  const LastSearch = (data) => {
    sessionStorage.setItem('DetalhesAnimal', JSON.stringify(data))
  }

  const changeModel = (data) => {
    const {
      animal,
      agAtual = {},
      caracteristicas = {},
      idCentral,
      genealogia = {},
      caracteristicasAgrupadas,
      percentilRaca = [],
    } = data

    // Observacao: 'genealogia' e um campo irmao de 'animal' na resposta (nao
    // contem uma chave 'animal' aninhada) - a checagem abaixo e mantida so por
    // seguranca. O .shift() que existia aqui antes removia sempre o PRIMEIRO
    // parente do array, presumindo que ele seria sempre o placeholder 'animal'
    // (o que nunca acontece) - isso descartava um parente real (ex: Pai, Mae)
    // sempre que a genealogia vinha completa. Removido.
    const _genealogias = Object.keys(genealogia)
      .filter((key) => key !== 'animal' && genealogia[key])
      .map((key) => {
        const title = (key === 'avoPai' && 'Avô Pai')
          || (key === 'avomPai' && 'Avó Pai')
          || (key === 'pai' && 'Pai')
          || (key === 'mae' && 'Mãe')
          || (key === 'avoMae' && 'Avô Mãe')
          || (key === 'avomMae' && 'Avó Mãe')
          || key

        const bgColor = (key === 'avoPai' || key === 'pai' || key === 'avoMae') && 'blue'
          || (key === 'avomMae' || key === 'avomPai' || key === 'mae') && 'pink'

        return {
          key: key,
          bgColor: bgColor,
          title: title,
          nome: genealogia[key].nome || '---',
          serie: genealogia[key].serie || '---',
          rgn: genealogia[key].rgn || '---',
          rgd: genealogia[key].rgd || '---',
          valor: `MGTe: ${genealogia[key].mgt || '---'}`,
          top: `TOP: ${genealogia[key].tmgt + '%' || '---'}`
        }
      })

    // Corte de MGTe (indice principal usado para classificar o animal no
    // selo Diamante/Esmeralda/Rubi/Safira/Topazio/TOP Maiores) na faixa de
    // percentil "top" da RACA do animal - vem da mesma tabela de percentis
    // ja usada no radar de DEPs (percentilRaca). Sem essa tabela (raca sem
    // dado, ou chamada falhou), volta pro texto fixo de antes, sem numero.
    const limiteMGTe = (topStr) => {
      const linha = Array.isArray(percentilRaca) && percentilRaca.find((r) => r.top === topStr)
      return linha && linha.mgt !== undefined ? linha.mgt : null
    }
    const textoFaixa = (topPct, comparador) => {
      const limite = limiteMGTe(`${topPct.toFixed(1)}%`)
      return limite !== null
        ? `- Top ${topPct}% (MGTe ${comparador} ${limite})`
        : `- Top ${topPct}%`
    }

    const model = {
      cga: animal.cga,
      title: agAtual.descricao || '---',
      nome: animal.nome || '---',
      proprietario: animal.nomeProprietario || '---',
      criador: animal.nomeCriador || '---',
      section_01: [
        {
          title: 'Série:',
          value: animal.serie || '---'
        },
        {
          title: 'RGN:',
          value: animal.rgn || '---'
        },
        {
          title: 'RGD:',
          value: animal.rgd || '---'
        },
        {
          title: 'Data de nascimento:',
          value: animal.dtNascimento ? format(new Date(animal.dtNascimento), "dd-MM-yyyy") : '---'
        },
        {
          title: 'Situação:',
          value: animal.situacao || '---'
        },
        {
          title: 'Reprodução programada:',
          value: animal.reproducaoProgramada || '---'
        }
      ],
      section_02: [
        {
          title: 'MGTe:',
          values: [
            {
              caract: caracteristicas.mgt || '---'
            },
            {
              caract: `@ ${caracteristicas.amgt}` || '---'
            },
            {
              caract: `® ${caracteristicas.tmgt || '---'}%`
            }
          ]
        },
        {
          title: 'Raça:',
          value: animal.nomeRaca || '---'
        },
        {
          title: 'Categoria:',
          value: animal.categoriaDescricao || '---'
        },
        {
          title: 'Variedade:',
          value: animal.variedadeDescricao || '---'
        },
        {
          title: 'Sexo:',
          value: animal.sexoDescricao || '---'
        },
        {
          title: 'Central:',
          value: idCentral || '---'
        },

      ],
      genealogia: _genealogias,
      caracteristicas,
      percentilRaca: Array.isArray(percentilRaca) ? percentilRaca : [],
      caracteristicasAgrupadas: Array.isArray(caracteristicasAgrupadas) ? caracteristicasAgrupadas : [],
      classification: [
        {
          key: 'diamante',
          str01: 'Diamante',
          str02: textoFaixa(1, '≥')
        },
        {
          key: 'esmeralda',
          str01: 'Esmeralda',
          str02: textoFaixa(5, '≥')
        },
        {
          key: 'rubi',
          str01: 'Rubi',
          str02: textoFaixa(10, '≥')
        },
        {
          key: 'safira',
          str01: 'Safira',
          str02: textoFaixa(20, '≥')
        },
        {
          key: 'topazio',
          str01: 'Topázio Imperial',
          str02: textoFaixa(30, '≥')
        },
        {
          key: 'top',
          str01: 'TOP Maiores',
          // "TOP Maiores" e o restante, abaixo do corte dos 30% (Topazio) -
          // por isso usa o mesmo corte de 30% mas com "<" em vez de "≥".
          str02: textoFaixa(30, '<')
        }
      ]
    }

    return model
  }

  const voltar = () => {
    setLink("animais-filtrados");
  };

  if (error) {
    return <div className="error-list">Error: {error}</div>;
  } else if (!isLoaded) {
    return <Loading show={true} />;
  } else {
    return (
      <>
        <div className="main-content">
          <div className="btn-container">
            <h3>Ficha técnica do Animal</h3>
            {
              (gerarPDF)
                ? (
                  <BlobProvider
                    document={
                      <PDFDocument
                        detalhesAnimal={detalhesAnimal && [detalhesAnimal]}
                      />}
                  >
                    {({ url, loading, error }) => {
                      if (error) {
                        console.error(error)
                      }
                      if (loading) {
                        return (
                          <button className="global-btn-info">
                            Criando PDF... <FontAwesomeIcon icon={faCircleNotch} pulse />
                          </button>
                        )
                      } else {
                        return (
                          <a
                            className={`global-btn-info`}
                            disabled={loading}
                            href={url}
                            target='_blank'
                          >
                            Abrir PDF
                          </a>
                        )
                      }
                    }}
                  </BlobProvider>
                )
                : (
                  <button className="global-btn-info"
                    onClick={() => setGerarPDF(true)}
                  >Gerar PDF</button>
                )
            }
          </div>

          <div className="cards" id="container-01">
            <div className="cards__card-container">
              <div className="cards__ul cards__ul--secondary">
                <div className="cards__title-container cards__secondary-container">
                  <h4 className="cards__title">
                    {detalhesAnimal && detalhesAnimal.title}
                  </h4>
                </div>
                <div className="card-row">
                  <div className="card-columns">
                    <div className="img-bck-container">
                      <div className="img-bck"></div>
                    </div>
                  </div>
                  <div className="card-columns">
                    <h2>
                      {detalhesAnimal && detalhesAnimal.nome || '---'}
                    </h2>
                    <p>
                      <h1>Proprietário: </h1>
                      {detalhesAnimal && detalhesAnimal.proprietario || '---'}
                    </p>
                    <p>
                      <h1>Criador: </h1>
                      {detalhesAnimal && detalhesAnimal.criador || '---'}
                    </p>
                  </div>
                  <div className="card-columns">
                    {detalhesAnimal && detalhesAnimal.section_01.map((item, i) =>
                      <div key={`sect-01-${i}`} className="cards__li">
                        <h1 className="cards__span-txt">{item.title}</h1>
                        <span className="cards__span">{item.value}</span>
                      </div>
                    )}
                  </div>
                  <div className="card-columns">
                    {detalhesAnimal && detalhesAnimal.section_02.map((item, i) => {
                      if (item.title === 'MGTe:') {
                        return (
                          <div key={`MGTe-${i}`} className="cards__li mgte">
                            <h1 className="cards__span-txt">{item.title}</h1>
                            <div className="cards__span-cont">
                              {item.values.map(mgte => (
                                <span key={`mgte-${mgte.caract}`} className="cards__span">{mgte.caract}</span>
                              ))}
                            </div>
                          </div>)
                      } else {
                        return (
                          <div key={`sect-02-${i}`} className="cards__li">
                            <h1 className="cards__span-txt">{item.title}</h1>
                            <span className="cards__span">{item.value}</span>
                          </div>)
                      }
                    })}
                  </div>
                </div>
                <div className="cards-info">
                  {detalhesAnimal && Array.isArray(detalhesAnimal.caracteristicasAgrupadas) && detalhesAnimal.caracteristicasAgrupadas.map((item, i) => {
                    if (i > 0)
                      return <CardInfo
                        key={`caract-${i}`}
                        nome={item.caracteristicaSigla}
                        valor={item.caracteristicas.dep}
                        acuracia={item.caracteristicas.acuracia}
                        top={item.caracteristicas.top}
                      />
                  })}

                  <div className="legend">
                    <p>@ Acurácia / ® Top / Dep G: DEP Genômica</p>
                    <p>Em destaques DEP’s que compõe o MTGe</p>
                  </div>

                  <div className="card-small">
                    <CardInfo
                      nome="NF3P"
                      valorN={0}
                    />

                    <CardInfo
                      nome="NN120"
                      valorN={0}
                    />

                    <CardInfo
                      nome="NRN120"
                      valorN={0}
                    />
                    <CardInfo
                      nome="NFSTAY"
                      valorN={0}
                    />
                    <CardInfo
                      nome="NF120"
                      valorN={0}
                    />
                    <CardInfo
                      nome="NR120"
                      valorN={0}
                    />
                    <CardInfo
                      nome="NF210"
                      valorN={0}
                    />
                    <CardInfo
                      nome="NR210"
                      valorN={0}
                    />
                    <CardInfo
                      nome="NF450"
                      valorN={0}
                    />
                    <CardInfo
                      nome="NR450"
                      valorN={0}
                    />
                    <CardInfo
                      nome="NFUS"
                      valorN={0}
                    />
                    <CardInfo
                      nome="NRUS"
                      valorN={0}
                    />
                    <CardInfo
                      nome="NFSAMS"
                      valorN={0}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="main-container" id="container-02">
          <h3>Genealogia</h3>
          <FamilyTree genealogia={detalhesAnimal && detalhesAnimal.genealogia} />
        </div>
        <div className="main-container">
          <h3>DEPs Gráficas</h3>
          <p className="radar-deps-caption">Perfil genético — quanto mais próximo da borda, melhor</p>
          <div className="depsContainer">
            <div className="depsCards depsCards--radar depsCards--radar-full">
              <div className="depsCardsContent depsCardsContent--radar">
                <RadarDEPs
                  dados={detalhesAnimal && detalhesAnimal.caracteristicas}
                  percentil={detalhesAnimal && detalhesAnimal.percentilRaca}
                  nomeAnimal={detalhesAnimal && detalhesAnimal.nome}
                />
                <div className="radar-legend-chips">
                  {detalhesAnimal && detalhesAnimal.classification.map((item, i) =>
                    // title = mesmo detalhe "Top X% (MGTe >= ...)" que antes
                    // ficava escrito no card separado - continua acessivel
                    // passando o mouse no chip, so nao fica mais escrito full-time.
                    <span key={`chip-${i}`} className="radar-legend-chip" title={item.str02}>
                      <span className={`radar-legend-dot depsText ${item.key}`}></span>
                      {item.str01}
                    </span>
                  )}
                </div>
              </div>
              <div className="depsCardsFooter">
                <div className="depsLogo"></div>
              </div>
            </div>
          </div>
        </div>
        <div className="main-content">
          <button onClick={voltar} className="global-btn-info">
            Voltar
          </button>
        </div>
      </>
    );
  }
}